/* @flow */
import { Observable } from 'rxjs';
import { browserHistory } from 'react-router';
import { combineEpics } from 'redux-observable';
import { prop, pipe, map } from 'ramda';
import { listToOrderedMap } from 'trustar/utils';
import { stringify } from 'querystring';
import { getQueryParams, parseServerErrorMessage } from 'trustar/utils';
import { reset as resetForm } from 'redux-form/immutable';
import { Record } from 'immutable';

import createDebugger from 'debug';
const debug = createDebugger('app:modules:settings:epics'); // eslint-disable-line no-unused-vars

import {
  SET_ENCLAVE_MENU,
  SELECT_DEFAULT_ENCLAVE,
  SAVE_EMAIL_SETTINGS,
  SAVE_EMAIL_SETTINGS_SUCCESS,
  SAVE_EMAIL_SETTINGS_FAILURE,
  FETCH_INGEST_EMAILS,
  FETCH_INGEST_EMAILS_SUCCESS,
  FETCH_INGEST_EMAILS_FAILURE,
  DELETE_INGEST_EMAIL,
  DELETE_INGEST_EMAIL_SUCCESS,
  DELETE_INGEST_EMAIL_FAILURE,
} from './action-types';
import * as Profile from 'modules/profile';
import * as Insights from 'modules/insights';
import { SAVE_DEFAULT_ENCLAVE } from 'modules/insights/action-types';
import { emailOptions as getEmailOptions } from './getters.js';
import { fetchIngestEmails } from './actions.js';

const EMAIL_SETTINGS_ENDPOINT = '/services/ingest-emails';

const navigateEpic = (action$) =>
  action$.ofType(SET_ENCLAVE_MENU)
    .filter(prop('payload'))
    .do(() => browserHistory.push('/settings/profile'))
    .delay(500)
    .do(() => {
      const el = document.getElementById('DefaultEnclaveSettings');
      if (el) el.scrollIntoView();
    })
    .ignoreElements();

const defaultEnclaveSelectionEpic = (action$, store, { ajax }) =>
  action$.ofType(SELECT_DEFAULT_ENCLAVE)
    .mergeMap(({ payload: { id, name }}) => {
      const updatedPreferences = {
        defaultEnclaveGuid: id
      };
      // Update profile, re-fetch insights data, and save default enclave
      return Observable.from([
        { type: SAVE_DEFAULT_ENCLAVE, payload: { id, name } },
        Profile.actions.updatePreferences(updatedPreferences),
      ]);
    });

const IngestEmail = Record({
  created: null,
  updated: null,
  createdBy: null,
  updatedBy: null,
  guid: null,
  ingestEmail: null,
  enclaveGuid: null,
  companyGuid: null,
  fromEmails: [],
  subjectPrefixes: [],
  groupThreads: true,
  purgeSubjectPrefix: false,
  enableRedaction: false,

  enclave: null, // This doesn't come from the server... we merge it in to make data rendering easier
});

const fromList = pipe(
  listToOrderedMap('guid'),
  map(IngestEmail)
);

const fetchIngestEmailsEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_INGEST_EMAILS)
    .map(prop('payload'))
    .mergeMap(params => {
      const url = EMAIL_SETTINGS_ENDPOINT + '?' + stringify(params);
      return ajax.get(url)
        .map(prop('response'))
        .map(page => {
          const payload = fromList(page.items);
          return {
            type: FETCH_INGEST_EMAILS_SUCCESS,
            payload,
            meta: {
              startPage: params.startPage,
              hasNext: page.hasNext,
              total: page.totalElements,
            },
          };
        })
        .catch(err => Observable.of({
          type: FETCH_INGEST_EMAILS_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: { type: 'error', text: 'Could not fetch ingest emails.' },
          },
        }));
    });

const deleteIngestEmailEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE_INGEST_EMAIL)
    .map(prop('payload'))
    .map(prop('guid'))
    .mergeMap(guid => {
      const url = EMAIL_SETTINGS_ENDPOINT + '/' + guid;
      return ajax.delete(url)
        .map(prop('response'))
        .map(prop('guid'))
        .map(guid => ({
          type: DELETE_INGEST_EMAIL_SUCCESS,
          payload: guid,
          meta: {
            alert: {
              type: 'success',
              text: 'Successfully deleted Enclave Inbox.'
            }
          }
        }))
        .catch(err => Observable.of({
          type: DELETE_INGEST_EMAIL_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {
              type: 'error',
              text: parseServerErrorMessage(err, 'Could not fetch ingest emails.'),
            },
          },
        }));
    });

const refetchIngestEmailsEpic = (action$, store) =>
  action$.ofType(SAVE_EMAIL_SETTINGS_SUCCESS)
    .map(() => getEmailOptions(store.getState()))
    .map(getQueryParams)
    .map(fetchIngestEmails);

/* Submit email settings form values to server */
const saveEmailSettingsEpic = (action$, store, { ajax }) =>
  action$
    .ofType(SAVE_EMAIL_SETTINGS)
    .map(({ payload: formValues }) => {
      return formValues
        .update('fromEmails', a => a.map(b => b.first()).filter(c => !!c))
        .update('subjectPrefixes', a => a.map(b => b.first()))
        .update('enclaveGuid', a => typeof a === 'string' ? a : a.value)
        .update('ingestEmail', x => x + '@enclave.trustar.co')
        .toJS();
    })
    .mergeMap(values => {
      const req$ = values.guid
        ? ajax.putJSON(EMAIL_SETTINGS_ENDPOINT + '/' + values.guid, values)
        : ajax.postJSON(EMAIL_SETTINGS_ENDPOINT, values);

      return req$
        .map(() => ({
          type: SAVE_EMAIL_SETTINGS_SUCCESS,
          meta: {
            alert: { type: 'success', text: 'Enclave Inbox saved. It may take up to two minutes to be available.' },
          },
        }))
        .mergeMap(action => [
          action,
          resetForm('createEmailIngest'), // Reset the create form if present
          resetForm('editEmailIngest'), // Reset the edit form if present
        ])
        .catch(err =>
          Observable.of({
            type: SAVE_EMAIL_SETTINGS_FAILURE,
            payload: err,
            error: true,
            meta: {
              alert: {
                type: 'error',
                text: parseServerErrorMessage(
                  err,
                  'Error saving email ingest.'
                ),
              },
            },
          })
        );
    });

export default combineEpics(
  refetchIngestEmailsEpic,
  fetchIngestEmailsEpic,
  deleteIngestEmailEpic,
  navigateEpic,
  defaultEnclaveSelectionEpic,
  saveEmailSettingsEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/settings/epics.js